import { ComponentProps, ReactNode, useEffect, useRef, useState } from "react";
import { Input as AntInput, InputRef, Space } from "antd";
import { useEffectOnce } from "usehooks-ts";

/**
 * This provides an input that:
 * 1. is wrapped in a form, so the user can press "Enter"
 * 2. debounces by waiting for submit
 * 3. watches for upstream changes, and resets
 * 4. allows for optional submit button
 * 5. passes the rest of the props to the Ant Input
 */

export function Input({
  value,
  onChange,
  submitNode,
  unsubmitNode,
  autoSelect,
  ...props
}: Omit<ComponentProps<typeof AntInput>, "value" | "onChange" | "ref"> & {
  value: string;
  onChange(val: string): void;
  submitNode?: ReactNode;
  unsubmitNode?: ReactNode;
  autoSelect?: boolean;
}) {
  const prev = useRef(value);
  const [internalValue, setValue] = useState(prev.current);
  const submittable = internalValue !== value;
  useEffect(() => {
    if (value !== prev.current) {
      setValue(value);
      prev.current = value;
    }
  }, [value]);
  const ref = useRef<InputRef>(null);
  useEffectOnce(() => {
    if (props.autoFocus)
      setTimeout(() => {
        if (ref.current) ref.current.focus();
      }, 0);
    if (autoSelect)
      setTimeout(() => {
        if (ref.current) ref.current.select();
      }, 0);
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onChange(internalValue);
      }}
    >
      <Space.Compact>
        <AntInput
          {...props}
          value={internalValue}
          onChange={(e) => setValue(e.target.value)}
          autoFocus={props.autoFocus}
          ref={ref}
        />
        {submittable && submitNode ? submitNode : null}
        {!submittable && submitNode ? unsubmitNode : null}
      </Space.Compact>
    </form>
  );
}
